/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, EventEmitter, Input} from '@angular/core';

import {Button} from './interfaces';

/**
 * Dialog modal presents the user with a custom dialog having variable number of buttons
 *
 * Typically used directly from the modal service using :
 *
 * ```
 * this.modalService.dialog({
 *   title: 'My dialog title',
 *   text: 'My dialog text',
 *   note: 'My note text',
 *   buttons: [
 *       {
 *         name: 'first',
 *         text: 'First button',
 *         style: 'primary'
 *       }
 *     ]
 * });
 * ```
 */
@Component({
  selector: 'gp-modal-dialog',
  template: `
    <div class="modal in" style="display: block;">
      <div class="modal-dialog" [ngClass]="{'modal-lg': size == 'lg'}">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ title | translate }}
            </h5>
          </div>
          <div class="modal-body p-lg">
            <div class="body-text">{{ text | translate }}</div>
            <div class="note-text">{{ note | translate }}</div>
          </div>
          <div class="modal-footer">
            <button *ngFor="let button of buttons"
                    type="button"
                    class="btn p-x-md {{button.style}}"
                    (click)="submit(button.name)"
                    test-id="modalButton">
              {{ button.text | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./dialog-modal.component.css']
})
export class DialogModalComponent {

  /**
   * @type {string} One of 'lg', ...
   */
  @Input()
  public size: string = '';

  /**
   * @type {string} Title of the modal
   */
  @Input()
  public title: string = '';

  /**
   * @type {string} Body text of the modal
   */
  @Input()
  public text: string = '';

  /**
   * @type {string} Note under body text of the modal
   */
  @Input()
  public note: string = '';

  /**
   * @type {Array<Button>} List of buttons to show for modal
   */
  @Input()
  public buttons: Array<Button>;

  /**
   * @type {EventEmitter<boolean>} Result of the dialog depending on user interaction
   */
  public result: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Button click
   */
  public submit(choice: any) {
    this.result.emit(choice);
    this.result.complete();
  }
}
